import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const SAB_SOURCE = 'https://webassembly.github.io/threads/core/shared/structs.html';
const COI_SOURCE = 'https://html.spec.whatwg.org/multipage/browsers.html#cross-origin-isolated';

/**
 * Process isolation and SharedArrayBuffer limits (novel_bot_detection §2D).
 *
 * Headless runners, minimal browser binaries, and cloud sandboxes (AWS Lambda,
 * locked-down CI) often disable cross-origin isolation or strictly cap
 * SharedArrayBuffer / Wasm memory to reduce footprint. This site sets
 * COOP: same-origin and COEP: credentialless via _headers, so a genuine
 * browser should report crossOriginIsolated === true and allocate SAB freely.
 */
export const sabProbe: Probe = {
  id: 'sab',
  label: 'Process isolation & SAB',
  layer: 'sab',
  tier: 'client',
  category: 'hardware',
  description: 'Cross-origin isolation, SharedArrayBuffer, and Atomics behaviour',
  budgetMs: 4000,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];
    const isolated = (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated === true;
    const hasSab = typeof SharedArrayBuffer === 'function';

    if (!hasSab) {
      return {
        signals: {
          crossOriginIsolated: ok(isolated, 'client'),
          sharedArrayBuffer: na<boolean>('unsupported', 'client', 'SharedArrayBuffer not exposed'),
        },
      };
    }

    // --- 1. allocation at escalating sizes --------------------------------
    // Sandboxes that cap memory fail at a specific size. We probe a ladder
    // and record where it breaks, if anywhere.
    const ladder = [1, 8, 64, 256, 1024]; // KiB
    const allocations: Array<{ kib: number; ok: boolean; error?: string }> = [];
    let maxAllocatedKiB = 0;
    for (const kib of ladder) {
      try {
        // SharedArrayBuffer takes bytes.
        const sab = new SharedArrayBuffer(kib * 1024);
        // Touch it so the allocation is committed, not just reserved.
        new Uint8Array(sab)[0] = 1;
        allocations.push({ kib, ok: true });
        maxAllocatedKiB = kib;
      } catch (err) {
        allocations.push({ kib, ok: false, error: String((err as Error)?.name ?? err) });
        break; // once it fails, larger will too
      }
    }
    const allocationCapped = maxAllocatedKiB < ladder[ladder.length - 1];

    // --- 2. Atomics.waitAsync latency -------------------------------------
    // Atomics.waitAsync is only allowed in a non-agents context; on a locked
    // down or non-isolated page it throws. We time a tiny async wait as a
    // scheduling-behaviour probe (it yields, it does not block).
    let atomicsAsync: 'available' | 'throws' | 'unknown' = 'unknown';
    let waitAsyncMs: number | null = null;
    try {
      const sab = new SharedArrayBuffer(4);
      const view = new Int32Array(sab);
      const t0 = performance.now();
      const res = Atomics.waitAsync(view, 0, 0, 0);
      atomicsAsync = 'available';
      if (res.async) await res.value;
      waitAsyncMs = performance.now() - t0;
    } catch (err) {
      atomicsAsync = 'throws';
      void err;
    }

    // --- 3. performance.measureUserAgentSpecificMemory -------------------
    let userAgentMemory: 'available' | 'unavailable' | 'error' = 'unavailable';
    let uamBytes: number | null = null;
    const memFn = (performance as Performance & {
      measureUserAgentSpecificMemory?: () => Promise<{ bytes: number }>;
    }).measureUserAgentSpecificMemory;
    if (typeof memFn === 'function') {
      try {
        const res = await memFn.call(performance);
        uamBytes = res.bytes;
        userAgentMemory = 'available';
      } catch {
        userAgentMemory = 'error';
      }
    }

    // --- 4. Wasm memory growth --------------------------------------------
    const wasmGrowth = measureWasmGrowth();

    // Findings. A non-isolated page with SAB present is unusual; a low SAB cap
    // is a sandbox signal. Both are medium confidence — privacy browsers can
    // also produce them.
    if (!isolated) {
      findings.push({
        id: 'sab.not-isolated',
        severity: 'low',
        category: 'hardware',
        layer: 'sab',
        confidence: 'low',
        title: 'Page is not cross-origin isolated',
        detail:
          'crossOriginIsolated is false despite this site requesting COOP/COEP. Isolation can be disabled by embedding, by a browser policy, or by a headless configuration. Weak on its own.',
        sources: [COI_SOURCE],
        evidence: ['crossOriginIsolated=false'],
        scoreContribution: 0,
      });
    }

    if (allocationCapped) {
      findings.push({
        id: 'sab.low-cap',
        severity: 'medium',
        category: 'hardware',
        layer: 'sab',
        confidence: 'medium',
        title: 'SharedArrayBuffer allocation capped below 1 MiB',
        detail:
          'A browser exposing SharedArrayBuffer but refusing allocations beyond a small ceiling is being memory-constrained, which is common in cloud functions and sandboxes. A normal desktop browser allocates multi-megabyte shared buffers without issue.',
        sources: [SAB_SOURCE],
        evidence: [`maxAllocatedKiB=${maxAllocatedKiB}`],
        scoreContribution: 15,
      });
    }

    return {
      signals: {
        crossOriginIsolated: ok(isolated, 'client'),
        sharedArrayBuffer: ok(true, 'client'),
        maxAllocatedKiB: ok(maxAllocatedKiB, 'client'),
        allocationCapped: ok(allocationCapped, 'derived'),
        allocationLadder: ok(allocations, 'client'),
        atomicsWaitAsync: ok(atomicsAsync, 'client'),
        waitAsyncMs: ok(waitAsyncMs == null ? null : sig(waitAsyncMs, 3), 'client'),
        measureUserAgentSpecificMemory: ok(userAgentMemory, 'client'),
        userAgentMemoryBytes: ok(uamBytes == null ? null : sig(uamBytes, 3), 'client'),
        wasmMaxPages: ok(wasmGrowth.maxPages, 'client'),
        wasmGrowOk: ok(wasmGrowth.growOk, 'client'),
      },
      findings,
      parts: {
        sab: {
          // Hash the capability shape, not live memory sizes (which track load).
          isolated,
          hasSab,
          atomicsAsync,
          wasmGrowOk: wasmGrowth.growOk,
        },
      },
    };
  },
};

function measureWasmGrowth(): { maxPages: number | null; growOk: boolean } {
  try {
    // (module (memory 1)) then grow as far as allowed without a max.
    const mod = new WebAssembly.Module(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ]),
    );
    const inst = new WebAssembly.Instance(mod, {});
    const mem = inst.exports.m as WebAssembly.Memory;
    let growOk = false;
    let pages = mem.buffer.byteLength / 65536;
    // Try to grow; a capped environment throws here.
    const before = mem.buffer.byteLength;
    mem.grow(64);
    growOk = mem.buffer.byteLength > before;
    pages = mem.buffer.byteLength / 65536;
    return { maxPages: pages, growOk };
  } catch {
    return { maxPages: null, growOk: false };
  }
}

registerProbe(sabProbe);
